import { useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import BottomNavigation from '@mui/material/BottomNavigation'
import BottomNavigationAction from '@mui/material/BottomNavigationAction'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import TodayIcon from '@mui/icons-material/Today'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SettingsIcon from '@mui/icons-material/Settings'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import AddIcon from '@mui/icons-material/Add'
import ThemeToggle from './ThemeToggle'
import CmdKPalette from './CmdKPalette'

const DRAWER_WIDTH = 240

const NAV_ITEMS = [
  { to: '/today', label: 'Today', icon: <TodayIcon /> },
  { to: '/library', label: 'Library', icon: <MenuBookIcon /> },
  { to: '/ask', label: 'Ask', icon: <AutoAwesomeIcon /> },
  { to: '/settings', label: 'Settings', icon: <SettingsIcon /> },
]

export default function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [paletteOpen, setPaletteOpen] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const currentIndex = NAV_ITEMS.findIndex((item) => location.pathname.startsWith(item.to))

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: 'var(--bg-lift)',
            borderRight: '1px solid var(--border)',
          },
        }}
      >
        <Box sx={{ px: 2.5, py: 2.5 }}>
          <Typography sx={{ fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 22, letterSpacing: '-0.4px', color: 'var(--ink)' }}>
            Recall
          </Typography>
        </Box>

        <Box sx={{ px: 2, pb: 1 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => navigate('/quiz')}
          >
            Start session
          </Button>
        </Box>

        <List dense sx={{ px: 0.5 }}>
          {NAV_ITEMS.map(({ to, label, icon }) => {
            const active = location.pathname.startsWith(to)
            return (
              <ListItem key={to} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => navigate(to)}
                  selected={active}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    color: active ? 'var(--moss)' : 'var(--ink-2)',
                    '&.Mui-selected': { bgcolor: 'var(--moss-soft)' },
                    '&.Mui-selected:hover': { bgcolor: 'var(--moss-soft)' },
                    '& .MuiListItemIcon-root': { color: active ? 'var(--moss)' : 'var(--ink-3)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
                  <ListItemText primary={label} slotProps={{ primary: { sx: { fontWeight: active ? 600 : 500 } } }} />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Drawer>

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 1.5,
            height: 60,
            px: 3,
            borderBottom: '1px solid var(--border)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'color-mix(in srgb, var(--bg) 82%, transparent)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Box
            component="button"
            onClick={() => setPaletteOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
              maxWidth: 520,
              height: 38,
              px: 1.5,
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              bgcolor: 'var(--surface)',
              color: 'var(--ink-3)',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
            }}
          >
            <span style={{ flex: 1, textAlign: 'left' }}>Search or jump to…</span>
            <kbd style={{ fontFamily: 'var(--font-mono)', fontSize: 11, padding: '2px 6px', borderRadius: 6, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>⌘K</kbd>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <ThemeToggle />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/library/add')}>
            Add book
          </Button>
        </Box>

        <Box
          component="main"
          sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, pb: { xs: 10, sm: 3 }, minWidth: 0 }}
        >
          {children}
        </Box>
      </Box>

      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, display: { sm: 'none' } }} elevation={3}>
        <BottomNavigation
          value={currentIndex}
          onChange={(_, newValue: number) => navigate(NAV_ITEMS[newValue].to)}
          showLabels
        >
          {NAV_ITEMS.map(({ label, icon }) => (
            <BottomNavigationAction key={label} label={label} icon={icon} />
          ))}
        </BottomNavigation>
      </Paper>

      <CmdKPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </Box>
  )
}
